import { inject, injectWithTransform, singleton } from "tsyringe";
import { CommunicationService, eventsRepository } from "@identity/services";
import {
  AdsContext,
  Logger,
  LoggerTransformer,
  TopLevelDomainExtractor,
  WikiConfig,
} from "@identity/utils";
import { COOKIES, WINDOW } from "@identity/symbols";

interface AgeGatePayload {
  adult?: boolean;
  timestamp: number;
}

@singleton()
export class AgeGateHandler {
  private readonly ageGateCookie = "ag";
  private readonly ageGateLegacyCookie = "age_gate";
  private readonly ageGateExpiryMs = 365 * 24 * 60 * 60 * 1000;

  constructor(
    @inject(COOKIES) private readonly cookiesManager: CookiesManager,
    @inject(WINDOW) private readonly window: Window,
    @inject(WikiConfig) private readonly wikiConfig: WikiConfig,
    @inject(AdsContext) private readonly context: AdsContext,
    @injectWithTransform(Logger, LoggerTransformer, "AgeGate")
    private readonly logger: Logger,
  ) {}

  // We inject communicationService via method because there were some problems
  // with tsyringe reporting circular dependency when trying to inject via constructor
  async init(communicationService: CommunicationService): Promise<void> {
    const wikiDirectedAtChildren = Boolean(
      this.context.get()?.targeting?.directedAtChildren,
    );

    const ageGateEnabled =
      wikiDirectedAtChildren && this.wikiConfig.get("wgAgeGateEnabled", false);

    if (!ageGateEnabled) {
      this.logger.info("disabled");
      return Promise.resolve();
    }

    // First version of the cookie has been set for 3 weeks and named "age_gate"
    // If it exists, change the name to "ag" and extend it to 1 year
    const legacyCookie = this.cookiesManager.get(this.ageGateLegacyCookie);
    if (legacyCookie) {
      this.updateLegacyCookie(legacyCookie);
      return Promise.resolve();
    }

    const ageGateCookie = this.cookiesManager.get(this.ageGateCookie);
    if (ageGateCookie) {
      return Promise.resolve();
    } else {
      await this.waitForAgeGateDataAndSetCookie(communicationService);
      return Promise.resolve();
    }
  }

  async waitForAgeGateDataAndSetCookie(
    communicationService: CommunicationService,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      communicationService.on(eventsRepository.AGE_GATE_DATA, ({ payload }) => {
        this.setCookie(payload);
        resolve();
      });
    });
  }

  setCookie(payload: AgeGatePayload | undefined): void {
    if (!payload) {
      this.logger.warn("Payload data missing, using defaults");
    }

    const encode = (value: boolean): string => (value ? "1" : "0");
    const adult = payload?.adult ?? false;
    const timestamp = payload?.timestamp ?? Date.now();

    this.cookiesManager.set(this.ageGateCookie, encode(adult), {
      domain: this.getTopLevelDomain(),
      expires: new Date(timestamp + this.ageGateExpiryMs),
    });
  }

  updateLegacyCookie(ageGateLegacyCookie): void {
    if (ageGateLegacyCookie.length >= 3) {
      const parts = ageGateLegacyCookie.split("|");
      this.setCookie({ adult: parts[1] === "1", timestamp: Date.now() });
    }

    this.cookiesManager.remove(this.ageGateLegacyCookie, {
      domain: this.getTopLevelDomain(),
    });
  }

  getTopLevelDomain(): string {
    const domain = this.window.location.hostname;
    return TopLevelDomainExtractor.extract(domain);
  }
}
